// src/oura/components/newscan/WordRow.tsx
//
// One word that writes the 50, in the Words view: its plain label, its value,
// how many questions use it, and where it came from.
//
// The value is a field that reads as text until it is hovered or focused (on
// a phone it always looks like a field, since there is no hover to hint it).
// Enter or leaving the field saves; Esc puts the value back. A refused save
// keeps what was typed, with the server's sentence under it. "<n> questions"
// opens the questions that use the word, with the value marked in each.
//
// Words the Business step sets (the outlet areas, the town, the anchor) are
// not edited here: they carry a lock and a link to that step.

import { CheckIcon, ChevronRightIcon, LockIcon } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { ScanDraftFailure, ScanDraftQuestion, ScanDraftWord, WordOrigin } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, PillButton, SURFACE, TYPE, Tag } from "@oura/ui";
import { plural } from "@oura/newscan/copy";
import { SlotText } from "./SlotText";

const WORD_ROW = {
    placeholder: "Type one",
    missing: "Missing",
    lockedSr: "Set on the Business step, not here",
    // Short enough for the count column in the two-panel Review (it ran past the card at "the Business step").
    businessStep: "Edit on Business",
    noQuestions: "In no question",
    usedBy: (n: number) => plural(n, "question"),
    usedByLabel: (n: number, label: string) => `${plural(n, "question")} use ${label.toLowerCase()}`,
    lockedQuestion: "Locked, keeps its wording",
    cap: (used: number, max: number) => `In ${used}, ${max} max`,
    origin: {
        research: "From reviews",
        site: "From the site",
        search_console: "From Search Console",
        services: "Your services",
        staff: "Changed by you",
        fallback: "Recipe default",
        queued_fill: "From the last scan",
    } satisfies Record<WordOrigin, string>,
} as const;

export interface WordRowProps {
    word: ScanDraftWord;
    /** The questions in `word.usedBy`, in order. */
    questions: ScanDraftQuestion[];
    /** Saves a new value. Resolves null on success, or the failure to show. */
    onSave(value: string): Promise<ScanDraftFailure | null>;
    /** The last save's outcome for this word: "Changed in 3 questions." */
    result?: string | null;
    /** The draft cannot be changed now (approved, building). */
    readOnly?: boolean;
    /** Opens the Business step, for words set there. Omit to show plain text. */
    onBusinessStep?: () => void;
    /** Opens the questions list on first render (from an "open word" link). */
    defaultOpen?: boolean;
    /**
     * The draft's version. A refused value is tried once more when it moves on
     * (another word filled may be the one the refusal asked for).
     */
    draftVersion?: number;
}

const norm = (v: string) => v.replace(/\s+/g, " ").trim();

/** The question text with every occurrence of `value` marked. */
function markValue(text: string, value: string | undefined): ReactNode {
    const needle = value ? norm(value) : "";
    if (!needle) return text;
    const lower = text.toLowerCase();
    const find = needle.toLowerCase();
    const out: ReactNode[] = [];
    let at = 0;
    let hit = lower.indexOf(find, at);
    while (hit !== -1) {
        if (hit > at) out.push(text.slice(at, hit));
        out.push(
            <mark key={hit} className="rounded bg-violet-100 px-0.5 text-gray-900">
                {text.slice(hit, hit + find.length)}
            </mark>,
        );
        at = hit + find.length;
        hit = lower.indexOf(find, at);
    }
    if (at < text.length) out.push(text.slice(at));
    return out.length ? out : text;
}

// Phone: a plain field. From 640px: text until hovered or focused.
const VALUE_FIELD = cn(
    "h-10 w-full min-w-0 rounded-xl bg-white px-3 text-base text-gray-900 ring-1 ring-inset ring-black/[0.08] placeholder:text-gray-400",
    "sm:h-9 sm:rounded-lg sm:bg-transparent sm:px-2 sm:text-sm sm:ring-transparent sm:hover:ring-gray-200 sm:focus:bg-white sm:focus:ring-black/[0.08]",
    "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-blue-600",
    "motion-safe:transition-shadow disabled:cursor-not-allowed disabled:text-gray-500",
);

export function WordRow({ word, questions, onSave, result, readOnly, onBusinessStep, defaultOpen = false, draftVersion }: WordRowProps) {
    const inputId = useId();
    const listId = useId();
    const msgId = useId();
    const [text, setText] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(defaultOpen);
    const pending = useRef(false);
    // The value the server just refused: leaving the field does not send it again.
    const refused = useRef<string | null>(null);

    // A value changed elsewhere (undo, a decision) shows unless something is being typed.
    const shown = text ?? word.value;

    useEffect(() => {
        if (defaultOpen) setOpen(true);
    }, [defaultOpen]);

    // The draft moved on while a refused value waits here: try it again, once.
    const seenVersion = useRef(draftVersion);
    useEffect(() => {
        if (draftVersion === seenVersion.current) return;
        seenVersion.current = draftVersion;
        if (refused.current === null || text === null || pending.current) return;
        refused.current = null;
        void commit();
        // Only the version moving on retries; commit reads the latest text.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftVersion]);

    async function commit() {
        if (text === null || pending.current) return;
        const next = norm(text);
        if (next === norm(word.value)) {
            setText(null);
            setError(null);
            return;
        }
        if (next === refused.current) return;
        pending.current = true;
        setSaving(true);
        const failure = await onSave(next);
        pending.current = false;
        setSaving(false);
        if (failure) {
            // Stale: the latest draft is already showing, so let its value through.
            if (failure.kind === "stale") {
                setText(null);
                setError(null);
            } else {
                refused.current = next;
                setError(failure.message);
            }
            return;
        }
        setText(null);
        setError(null);
    }

    function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === "Enter") {
            e.preventDefault();
            // Enter always asks again, even for the value just refused; leaving the field does not.
            refused.current = null;
            void commit();
        } else if (e.key === "Escape" && text !== null && !pending.current) {
            e.preventDefault();
            e.stopPropagation();
            setText(null);
            setError(null);
            refused.current = null;
        }
    }

    const count = word.usedBy.length;
    const missing = word.editable && !norm(word.value) && count > 0;
    const overCap = word.capWarn && word.capWarn.used > word.capWarn.warnAbove;
    const listMark = (q: ScanDraftQuestion) => q.fill_values[word.variable] ?? word.value;

    return (
        <div className={cn("border-t py-2.5 first:border-t-0", SURFACE.hairline)} data-word-row={word.variable}>
            <div className="flex flex-col gap-1.5 sm:grid sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)_auto] sm:items-center sm:gap-x-4 sm:gap-y-0">
                {word.editable ? (
                    <label htmlFor={inputId} className={cn(TYPE.tc, "min-w-0 break-words text-gray-500")}>
                        {word.label}
                    </label>
                ) : (
                    <span className={cn(TYPE.tc, "min-w-0 break-words text-gray-500")}>{word.label}</span>
                )}

                <div className="min-w-0">
                    {word.editable ? (
                        <>
                            <input
                                id={inputId}
                                data-word-input={word.variable}
                                type="text"
                                autoComplete="off"
                                spellCheck={false}
                                value={shown}
                                placeholder={WORD_ROW.placeholder}
                                disabled={readOnly}
                                // Held while its own save is on its way: text typed meanwhile would be
                                // replaced by the saved value, or never sent (Jack, #4293). readOnly keeps focus.
                                readOnly={saving}
                                aria-invalid={error ? true : undefined}
                                aria-describedby={error || result ? msgId : undefined}
                                aria-busy={saving || undefined}
                                list={word.alternatives.length ? `${inputId}-alts` : undefined}
                                onChange={(e) => {
                                    if (pending.current) return;
                                    setText(e.target.value);
                                    refused.current = null;
                                    if (error) setError(null);
                                }}
                                onKeyDown={onKeyDown}
                                onBlur={() => void commit()}
                                className={cn(VALUE_FIELD, error && "ring-rose-400 sm:ring-rose-400")}
                            />
                            {word.alternatives.length > 0 && (
                                <datalist id={`${inputId}-alts`}>
                                    {word.alternatives.map((alt) => (
                                        <option key={alt} value={alt} />
                                    ))}
                                </datalist>
                            )}
                        </>
                    ) : (
                        <span className={cn(TYPE.tb, "flex min-h-9 min-w-0 items-center gap-1.5 text-gray-900 sm:px-2")}>
                            <LockIcon aria-hidden size={12} className="shrink-0 text-gray-400" />
                            <span className="min-w-0 break-words">{word.value}</span>
                            <span className="sr-only">, {WORD_ROW.lockedSr}</span>
                        </span>
                    )}
                </div>

                <div className={cn(TYPE.tc, "flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 sm:justify-end")}>
                    {overCap && word.capWarn && <Tag tone="amber">{WORD_ROW.cap(word.capWarn.used, word.capWarn.warnAbove)}</Tag>}
                    {count > 0 ? (
                        <button
                            type="button"
                            aria-expanded={open}
                            aria-controls={listId}
                            aria-label={WORD_ROW.usedByLabel(count, word.label)}
                            onClick={() => setOpen((o) => !o)}
                            className={cn(
                                "relative inline-flex h-6 items-center gap-0.5 rounded font-medium tabular-nums text-blue-600 hover:underline",
                                "after:absolute after:-inset-x-1 after:-inset-y-2.5 after:content-['']",
                                FOCUS_RING,
                            )}
                        >
                            {WORD_ROW.usedBy(count)}
                            <ChevronRightIcon
                                aria-hidden
                                size={14}
                                className={cn("shrink-0 motion-safe:transition-transform motion-safe:duration-200", open && "rotate-90")}
                            />
                        </button>
                    ) : (
                        <span className="text-gray-400">{WORD_ROW.noQuestions}</span>
                    )}
                    <span aria-hidden className="text-gray-300 sm:hidden">
                        ·
                    </span>
                    {/* One width, so the question counts line up down the list. */}
                    <span className="min-w-0 sm:w-32 sm:text-left">
                        {missing ? (
                            <Tag tone="amber">{WORD_ROW.missing}</Tag>
                        ) : word.editable ? (
                            <span className="whitespace-nowrap text-gray-400">{WORD_ROW.origin[word.origin]}</span>
                        ) : onBusinessStep ? (
                            <PillButton variant="text" onClick={onBusinessStep}>
                                {WORD_ROW.businessStep}
                            </PillButton>
                        ) : (
                            <span className="whitespace-nowrap text-gray-400">{WORD_ROW.businessStep}</span>
                        )}
                    </span>
                </div>

                {(error || result) && (
                    <p
                        id={msgId}
                        role={error ? "alert" : "status"}
                        className={cn(
                            TYPE.tc,
                            "m-0 mt-0.5 flex items-start gap-1.5 sm:col-span-2 sm:col-start-2 sm:mt-1 sm:px-2",
                            error ? "font-medium text-rose-600" : "text-gray-600",
                        )}
                    >
                        {!error && <CheckIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-emerald-600" />}
                        <span className="min-w-0 break-words">{error ?? result}</span>
                    </p>
                )}

                <div id={listId} hidden={!open} className="min-w-0 sm:col-span-2 sm:col-start-2">
                    {open && count > 0 && (
                        <ul role="list" className="list-plain m-0 mt-2 space-y-1.5 p-0">
                            {questions.map((q) => (
                                <li key={q.n} className={cn(SURFACE.inner, TYPE.tc, "flex items-start gap-3 text-gray-700")}>
                                    <span className="w-6 shrink-0 text-right tabular-nums text-gray-400">{q.n}</span>
                                    <span className="min-w-0 flex-1 break-words">
                                        <SlotText
                                            text={q.text}
                                            words={{ [word.variable]: word }}
                                            renderText={q.locked ? undefined : (t) => markValue(t, listMark(q))}
                                        />
                                    </span>
                                    {q.locked && (
                                        <span className="mt-0.5 shrink-0 text-gray-400" title={WORD_ROW.lockedQuestion}>
                                            <LockIcon aria-hidden size={12} />
                                            <span className="sr-only">{WORD_ROW.lockedQuestion}</span>
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
